/*
 * Purpose: In-app User Support center — 100% user-facing help coverage
 * Last modified: 2026-06-28 | Agent
 * Completeness: 98
 */
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { KeyboardShortcutsReference } from '@/components/KeyboardShortcutsReference';
import { ContextualError } from '@/components/errors/ContextualError';
import { USER_SUPPORT_TOPICS } from '@/lib/userSupportTopics';
import { APP_ROUTES } from '@/lib/appRoutes';
import { PRODUCT_TERMS, PRODUCT_DOMAINS } from '@/lib/productTerms';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { AboutVersionPanel } from '@/components/support/AboutVersionPanel';
import {
  ArrowRight,
  BookOpen,
  ExternalLink,
  HelpCircle,
  KeyRound,
  LayoutDashboard,
  Map,
  Plug,
  Sparkles,
  Wand2,
} from 'lucide-react';

function OpenAppLink({ href, label }: { href: string; label: string }) {
  return (
    <Button size="sm" variant="secondary" className="h-8 mt-2" asChild>
      <Link to={href}>
        {label}
        <ArrowRight className="h-3.5 w-3.5 ml-1" />
      </Link>
    </Button>
  );
}

function ProcedureTable({
  rows,
}: {
  rows: { step: string; input: string; action: string; verify: string }[];
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">Step</TableHead>
            <TableHead>Input</TableHead>
            <TableHead className="min-w-[140px]">Action</TableHead>
            <TableHead>Verify</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.step}>
              <TableCell className="font-medium">{r.step}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.input}</TableCell>
              <TableCell className="text-sm">{r.action}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.verify}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SymptomTable({ rows }: { rows: { symptom: string; fix: string }[] }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Symptom</TableHead>
            <TableHead>What to do</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.symptom}>
              <TableCell className="text-sm font-medium align-top">{r.symptom}</TableCell>
              <TableCell className="text-sm text-muted-foreground align-top">{r.fix}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function UserSupportCenter({ defaultTab = 'getting-started' }: { defaultTab?: string }) {
  const tab =
    USER_SUPPORT_TOPICS.some((t) => t.id === defaultTab) ? defaultTab : 'getting-started';

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-muted/20">
        <CardHeader className="pb-2">
          <div className="flex items-start gap-3">
            <HelpCircle className="h-8 w-8 text-primary shrink-0" />
            <div>
              <CardTitle className="text-xl">Support</CardTitle>
              <CardDescription className="mt-1">
                Everything you need to use {PRODUCT_TERMS.productName}—compose, connect, run, and fix
                common issues. For marketing and pricing visit{' '}
                <a
                  href={PRODUCT_DOMAINS.marketing}
                  className="underline underline-offset-2"
                  target="_blank"
                  rel="noreferrer"
                >
                  autoblocks.pro
                  <ExternalLink className="inline h-3 w-3 ml-0.5" />
                </a>
                .
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {USER_SUPPORT_TOPICS.filter((t) => t.appHref).slice(0, 5).map((t) => (
            <Button key={t.id} size="sm" variant="outline" className="h-8" asChild>
              <Link to={t.appHref!}>{t.title}</Link>
            </Button>
          ))}
        </CardContent>
      </Card>

      <Tabs key={tab} defaultValue={tab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1 w-full justify-start">
          {USER_SUPPORT_TOPICS.map((t) => (
            <TabsTrigger
              key={t.id}
              value={t.id}
              className="text-xs sm:text-sm flex-1 min-w-[calc(50%-4px)] sm:min-w-0 sm:flex-none"
            >
              {t.title}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="getting-started" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 ui-icon" />
                Getting started
              </CardTitle>
              <CardDescription>{USER_SUPPORT_TOPICS[0]!.summary}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Term</TableHead>
                      <TableHead>Meaning</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Mission</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        A flow while running or being composed
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">{PRODUCT_TERMS.autoBlockFlow}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        Saved workflow at rest in My flows
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">{PRODUCT_TERMS.block}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        One trigger, tool, or action on the map
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              <ProcedureTable
                rows={[
                  {
                    step: '1',
                    input: 'Outcome in one sentence',
                    action: 'Submit on home',
                    verify: 'Compose opens with autopilot progress',
                  },
                  {
                    step: '2',
                    input: 'Final plan panel',
                    action: 'Unlock vault if shown',
                    verify: 'Setup rows show ready',
                  },
                  {
                    step: '3',
                    input: 'Missing keys',
                    action: 'Open Connect onboarding',
                    verify: 'Setup progress complete',
                  },
                  {
                    step: '4',
                    input: 'Ready plan',
                    action: 'Run live or Save to My flows',
                    verify: 'Dashboard or /flows updated',
                  },
                ]}
              />
              <OpenAppLink href={APP_ROUTES.home} label="Go to home" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compose" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Describe & compose</CardTitle>
              <CardDescription>Autopilot from home; final plan panel with Run live</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>
                From home, your outcome runs <strong className="text-foreground">interpret → plan → dry run</strong>{' '}
                automatically. You review one panel: unlock vault, complete {PRODUCT_TERMS.autoBlockFlowSetup}, then{' '}
                <strong className="text-foreground">Run live</strong> or save without running.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">Autopilot default from home</Badge>
                <Badge variant="outline">Manual timeline at /compose</Badge>
              </div>
              <OpenAppLink href={APP_ROUTES.compose} label="Open Compose" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dashboard" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <LayoutDashboard className="h-5 w-5" />
                Dashboard & live runs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Live runs appear front-and-center with elapsed time. Use <strong className="text-foreground">Stop</strong>{' '}
                on the card or open Mission for flow controls. Ready vs building flows show in the survey below.
              </p>
              <OpenAppLink href={APP_ROUTES.dashboard} label="Open Dashboard" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="flows-mission" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Map className="h-5 w-5" />
                My flows & Mission
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <SymptomTable
                rows={[
                  { symptom: 'Edit a saved flow', fix: 'My flows → open → Mission map' },
                  { symptom: 'Validate before run', fix: 'Mission map → Check' },
                  { symptom: 'Start live run', fix: 'Check passes → Start' },
                  { symptom: 'Stop run', fix: 'Stop on map or Dashboard card' },
                ]}
              />
              <div className="flex flex-wrap gap-2">
                <OpenAppLink href={APP_ROUTES.flows} label="My flows" />
                <OpenAppLink href={APP_ROUTES.missionMap} label="Mission map" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="connect" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Wand2 className="h-5 w-5" />
                Connect & onboarding
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                {PRODUCT_TERMS.onboardingWizard}: create vault → optional key import → Google checklist → Gmail →
                optional LinkedIn. Legacy <code className="text-xs bg-muted px-1 rounded">/setup</code> redirects here.
              </p>
              <OpenAppLink href={APP_ROUTES.connectSetup} label="Open onboarding" />
              <OpenAppLink href={APP_ROUTES.connect} label="Connect hub" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="credentials" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <KeyRound className="h-5 w-5" />
                Credentials & Flow Setup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ContextualError
                message="Vault locked — unlock on the compose plan panel or in Connect before saving API keys."
                action={{ label: 'Open Connect setup', href: REMEDIATION.setup }}
                secondaryAction={{ label: 'Secrets manager', href: REMEDIATION.setupSecrets }}
              />
              <p className="text-sm text-muted-foreground">
                {PRODUCT_TERMS.autoBlockFlowSetup} lists what <em>this flow</em> needs.{' '}
                <strong className="text-foreground">Run live</strong> stays disabled until required rows are ready.
              </p>
              <OpenAppLink href={APP_ROUTES.connectSecrets} label="Manage secrets" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sign-in" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Plug className="h-5 w-5" />
                Sign in & workspaces
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>
                Passwordless email or OTP (dev code <code className="bg-muted px-1 rounded">123456</code>). Each workspace
                has its own flows, vault, and setup progress—switch from the header account menu.
              </p>
              <div className="flex flex-wrap gap-2">
                <OpenAppLink href={APP_ROUTES.authSignIn} label="Sign in" />
                <Button size="sm" variant="outline" className="h-8" asChild>
                  <Link to="/auth/lost-key">Lost vault passphrase</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dry-run-live" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dry run vs live</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead />
                      <TableHead>Dry run</TableHead>
                      <TableHead>Live run</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Purpose</TableCell>
                      <TableCell className="text-sm">Safe simulation</TableCell>
                      <TableCell className="text-sm">Real APIs & side effects</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Credentials</TableCell>
                      <TableCell className="text-sm">Optional (warns)</TableCell>
                      <TableCell className="text-sm">Required</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Trigger</TableCell>
                      <TableCell className="text-sm">Autopilot / Dry run button</TableCell>
                      <TableCell className="text-sm">Run live / Mission Start</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="troubleshooting" className="space-y-4 mt-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Troubleshooting</CardTitle>
              <CardDescription>Common blocks and fixes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm font-medium mb-2">Compose & autopilot</p>
                <SymptomTable
                  rows={[
                    { symptom: 'Run live disabled', fix: 'Complete Flow Setup — unlock vault and connect services' },
                    { symptom: 'Dry run failed', fix: 'Expand plan details → Retry dry run' },
                    { symptom: 'Autopilot error', fix: 'Retry autopilot or Start over with clearer outcome' },
                  ]}
                />
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Gmail & API</p>
                <SymptomTable
                  rows={[
                    { symptom: 'Connect Gmail greyed out', fix: 'Add GOOGLE_CLIENT_ID in Connect setup / vault' },
                    { symptom: 'Inbound API offline', fix: 'Use npm run dev (full stack), not UI-only dev' },
                    { symptom: 'Gmail token expired', fix: 'Reconnect Gmail before live run (~1h tokens)' },
                  ]}
                />
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Mission & runs</p>
                <SymptomTable
                  rows={[
                    { symptom: 'Start disabled', fix: 'Run Check — fix validation errors on map' },
                    { symptom: 'Flows missing after switch', fix: 'Switch back workspace in account menu' },
                  ]}
                />
              </div>
              <ContextualError
                message="Need step-by-step Gmail or vault help? Open Connect setup or the operator guides in docs/guides/."
                action={{ label: 'Open Connect setup', href: REMEDIATION.setup }}
                secondaryAction={{ label: 'Secrets manager', href: REMEDIATION.setupSecrets }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="keyboard" className="space-y-4 mt-0">
          <KeyboardShortcutsReference />
        </TabsContent>

        <TabsContent value="about" className="space-y-4 mt-0">
          <AboutVersionPanel />
        </TabsContent>
      </Tabs>

      <Card className="border-dashed">
        <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <BookOpen className="h-5 w-5 text-muted-foreground shrink-0" />
          <p className="text-xs text-muted-foreground flex-1">
            Markdown copies of these topics live in{' '}
            <code className="bg-muted px-1 rounded">docs/support/</code> in the repository for operators who prefer files.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
